/**
 * GlobalMarketsTab — sub-tabs for US Indices, Canadian Indices, Commodities.
 * Follows the same UX pattern as IndicesTab (cards, favorites, search, modal).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getMarketsQuotes, getMarketsAnalytics, getMarketsFundamentals } from '../../services/marketsService';
import MarketDetailModal from './MarketDetailModal';
import '../../styles/FundsPage.css';

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

const SUB_TABS = [
    { key: 'us', label: 'US Indices' },
    { key: 'ca', label: 'Canadian Indices' },
    { key: 'commodities', label: 'Commodities' },
];

const FAVOURITES_KEY = 'finlytics:global-markets-favourites';
const FAV_ONLY_KEY = 'finlytics:global-markets-show-fav-only';

const isMetricUnavailable = (val) => val == null || !Number.isFinite(Number(val)) || Number(val) === 0;
const formatMetric = (val) => (isMetricUnavailable(val) ? 'N/A' : Number(val).toFixed(2));
const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`);

const GlobalMarketsTab = () => {
    const [subTab, setSubTab] = useState('us');
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    // Group summary state
    const [groupSummary, setGroupSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);

    // Favorites stored as { us: ['symbol1'], ca: [...], commodities: [...] }
    const [favourites, setFavourites] = useState(() => {
        try {
            const raw = localStorage.getItem(FAVOURITES_KEY);
            return raw ? JSON.parse(raw) : { us: [], ca: [], commodities: [] };
        } catch { return { us: [], ca: [], commodities: [] }; }
    });
    const [showFavOnly, setShowFavOnly] = useState(() => {
        const saved = localStorage.getItem(FAV_ONLY_KEY);
        return saved === '1';
    });

    // Persist favorites
    useEffect(() => {
        localStorage.setItem(FAVOURITES_KEY, JSON.stringify(favourites));
    }, [favourites]);

    useEffect(() => {
        localStorage.setItem(FAV_ONLY_KEY, showFavOnly ? '1' : '0');
    }, [showFavOnly]);

    // Fetch quotes when sub-tab changes
    const fetchQuotes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const raw = await getMarketsQuotes(subTab);
            const data = unwrap(raw);
            setQuotes(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch market quotes:', err);
            setError(
                err?.response?.status === 0 || !err?.response
                    ? 'Unable to reach market data service. Ensure the backend is running.'
                    : `Failed to load quotes: ${err.message}`
            );
        } finally {
            setLoading(false);
        }
    }, [subTab]);

    useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);

    // Compute group summary from quotes once loaded
    useEffect(() => {
        if (!quotes.length) { setGroupSummary(null); return; }

        let cancelled = false;
        setSummaryLoading(true);

        const computeSummary = async () => {
            const isCmdty = subTab === 'commodities';
            const analyticsResults = [];
            const fundamentalsResults = [];

            // Fetch analytics + fundamentals for each symbol in parallel
            const promises = quotes.map(async (q) => {
                try {
                    const raw = await getMarketsAnalytics(q.symbol, 'MAX');
                    const result = unwrap(raw);
                    if (result && !result.error) analyticsResults.push(result);
                } catch { /* skip */ }

                if (!isCmdty && q.hasFundamentals) {
                    try {
                        const raw = await getMarketsFundamentals(q.symbol);
                        const result = unwrap(raw);
                        if (result) fundamentalsResults.push(result);
                    } catch { /* skip */ }
                }
            });

            await Promise.all(promises);
            if (cancelled) return;

            const median = (arr) => {
                if (!arr.length) return null;
                const s = [...arr].sort((a, b) => a - b);
                const mid = Math.floor(s.length / 2);
                return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
            };
            const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
            const round2 = (v) => (v != null ? Math.round(v * 100) / 100 : null);

            const cagrs = { '1y': [], '3y': [], '5y': [], '10y': [] };
            for (const a of analyticsResults) {
                for (const span of ['1y', '3y', '5y', '10y']) {
                    const v = a[`cagr_${span}`];
                    if (v != null) cagrs[span].push(v);
                }
            }

            const summary = { count: quotes.length };
            for (const span of ['1y', '3y', '5y', '10y']) {
                summary[`cagr_${span}_median`] = round2(median(cagrs[span]));
                summary[`cagr_${span}_avg`] = round2(avg(cagrs[span]));
            }

            if (!isCmdty) {
                const pes = fundamentalsResults.map((f) => f.trailingPE).filter((v) => v != null);
                const pbs = fundamentalsResults.map((f) => f.priceToBook).filter((v) => v != null);
                summary.pe_median = round2(median(pes));
                summary.pe_avg = round2(avg(pes));
                summary.pb_median = round2(median(pbs));
                summary.pb_avg = round2(avg(pbs));
            }

            setGroupSummary(summary);
            setSummaryLoading(false);
        };

        computeSummary();
        return () => { cancelled = true; };
    }, [quotes, subTab]);

    // Toggle favourite
    const toggleFavourite = (symbol) => {
        setFavourites((prev) => {
            const groupFavs = prev[subTab] || [];
            const upper = symbol.toUpperCase();
            const next = groupFavs.includes(upper)
                ? groupFavs.filter((s) => s !== upper)
                : [...groupFavs, upper];
            return { ...prev, [subTab]: next };
        });
    };

    // Filtered + searched quotes
    const filteredQuotes = useMemo(() => {
        let list = quotes;
        if (showFavOnly) {
            const groupFavs = favourites[subTab] || [];
            list = list.filter((q) => groupFavs.includes(q.symbol?.toUpperCase()));
        }
        if (searchQuery.trim()) {
            const lower = searchQuery.toLowerCase();
            list = list.filter(
                (q) =>
                    (q.name || '').toLowerCase().includes(lower) ||
                    (q.symbol || '').toLowerCase().includes(lower)
            );
        }
        return list;
    }, [quotes, showFavOnly, favourites, subTab, searchQuery]);

    const groupFavs = favourites[subTab] || [];

    // ─────── Render ───────

    if (loading) {
        return (
            <div>
                <SubTabBar subTab={subTab} setSubTab={setSubTab} />
                <div className="funds-loading">Loading market data…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <SubTabBar subTab={subTab} setSubTab={setSubTab} />
                <div className="funds-error">
                    <p>{error}</p>
                    <button className="mf-search-btn" style={{ marginTop: 12 }} onClick={fetchQuotes}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <SubTabBar subTab={subTab} setSubTab={setSubTab} />

            {/* Group summary strip */}
            {groupSummary && !summaryLoading && (
                <div className="market-group-summary">
                    <span className="market-summary-label">Group Summary ({groupSummary.count} items)</span>
                    <span className="market-summary-chip">1Y CAGR med: {fmtPct(groupSummary.cagr_1y_median)}</span>
                    <span className="market-summary-chip">3Y: {fmtPct(groupSummary.cagr_3y_median)}</span>
                    <span className="market-summary-chip">5Y: {fmtPct(groupSummary.cagr_5y_median)}</span>
                    {groupSummary.pe_median != null && (
                        <span className="market-summary-chip">PE med: {formatMetric(groupSummary.pe_median)}</span>
                    )}
                    {groupSummary.pb_median != null && (
                        <span className="market-summary-chip">P/B med: {formatMetric(groupSummary.pb_median)}</span>
                    )}
                </div>
            )}
            {summaryLoading && (
                <div className="market-group-summary" style={{ opacity: 0.6 }}>
                    <span className="market-summary-label">Computing group summary…</span>
                </div>
            )}

            {/* Toolbar */}
            <div className="indices-toolbar">
                <input
                    className="mf-search-input"
                    placeholder="Search by name or symbol…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                    className={`indices-fav-toggle ${showFavOnly ? 'indices-fav-toggle--active' : ''}`}
                    onClick={() => setShowFavOnly((prev) => !prev)}
                    type="button"
                >
                    {showFavOnly ? 'Showing Favourites' : 'Show Favourites Only'}
                </button>
                <div className="indices-filter-meta">
                    {filteredQuotes.length} of {quotes.length}
                </div>
            </div>

            {/* Cards grid */}
            {filteredQuotes.length === 0 ? (
                <div className="funds-empty" style={{ padding: '1rem 0 1.75rem' }}>
                    <p>{showFavOnly ? 'No favourites match your search.' : 'No items match your search.'}</p>
                    {showFavOnly && groupFavs.length === 0 && (
                        <button
                            className="mf-search-btn"
                            style={{ marginTop: 10 }}
                            onClick={() => setShowFavOnly(false)}
                        >
                            Show All
                        </button>
                    )}
                </div>
            ) : (
                <div className="indices-grid">
                    {filteredQuotes.map((q) => {
                        const symbol = (q.symbol || '').toUpperCase();
                        const isFav = groupFavs.includes(symbol);
                        const positive = (q.change ?? 0) >= 0;
                        const colorClass = positive ? 'index-change-positive' : 'index-change-negative';
                        const isCmdty = subTab === 'commodities';

                        return (
                            <div
                                key={q.symbol}
                                className="index-card"
                                onClick={() => setSelectedItem(q)}
                                tabIndex={0}
                                role="button"
                                aria-label={`${q.name} — ${q.last ?? 'N/A'}`}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedItem(q);
                                    }
                                }}
                            >
                                <div className="index-card-header">
                                    <h3 className="index-card-name">{q.name}</h3>
                                    <div className="index-card-header-actions">
                                        <button
                                            className={`index-fav-btn ${isFav ? 'index-fav-btn--active' : ''}`}
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavourite(symbol);
                                            }}
                                            aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
                                            title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                                        >
                                            ★
                                        </button>
                                        <span className="index-card-symbol">{q.symbol}</span>
                                    </div>
                                </div>

                                <div className="index-card-value">
                                    {q.last != null ? q.last.toLocaleString('en-US') : '—'}
                                </div>

                                <div className="index-card-change">
                                    <span className={colorClass}>
                                        {positive ? '+' : ''}{q.change ?? '—'}
                                    </span>
                                    <span className={colorClass}>
                                        ({positive ? '+' : ''}{q.changePercent ?? '—'}%)
                                    </span>
                                </div>

                                {!isCmdty && q.hasFundamentals && (
                                    <div className="index-card-metrics">
                                        <span className="index-metric-chip">PE via ETF</span>
                                    </div>
                                )}
                                {isCmdty && (
                                    <div className="index-card-metrics">
                                        <span className="index-metric-chip">Commodity</span>
                                    </div>
                                )}

                                {q.timestamp && (
                                    <div className="index-card-updated">
                                        Updated: {new Date(q.timestamp).toLocaleString()}
                                    </div>
                                )}

                                {q.error && (
                                    <div className="index-card-updated" style={{ color: 'var(--warning)' }}>
                                        ⚠ Data may be delayed
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedItem && (
                <MarketDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                />
            )}
        </>
    );
};

/** Sub-tab navigation bar */
const SubTabBar = ({ subTab, setSubTab }) => (
    <div className="market-subtabs">
        {SUB_TABS.map((tab) => (
            <button
                key={tab.key}
                className={`market-subtab ${subTab === tab.key ? 'market-subtab--active' : ''}`}
                onClick={() => setSubTab(tab.key)}
            >
                {tab.label}
            </button>
        ))}
    </div>
);

export default GlobalMarketsTab;
